import styles from './layout.module.css';
import logo from '../../assets/logo_poem.gif';
import BmweMenu from '../menu/bmweMenu';
import { Outlet } from '@tanstack/react-router';
import { Image } from 'primereact/image';
import { Button } from 'primereact/button';
import { ReactNode, useCallback, useState } from 'react';
import { MenuItem } from 'primereact/menuitem';
import { commonStrings } from '../../i18n';
import { APP_VERSION } from './appVersion';

const SIDEBAR_STORAGE_KEY = 'bmwe-sidebar-collapsed';

function loadCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveCollapsed(value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // ignore
  }
}

export interface LayoutProps {
  user?: ReactNode;
  items: MenuItem[];
}

export function Layout({ user, items }: LayoutProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => loadCollapsed());

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      saveCollapsed(next);
      return next;
    });
  }, []);

  const toggleLabel = collapsed
    ? commonStrings.menu.expand
    : commonStrings.menu.collapse;

  return (
    <div
      className={styles['layout']}
      data-sidebar-collapsed={collapsed ? 'true' : 'false'}
    >
      <div className={styles['header']}>
        <Image src={logo} height="50" alt="POEM - Logo" />
        {user && user}
      </div>
      <div className={styles['menu']}>
        <div className={styles['menuToggle']}>
          <Button
            icon={collapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'}
            text
            severity="secondary"
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            title={toggleLabel}
            onClick={toggleCollapsed}
          />
        </div>
        {!collapsed && <BmweMenu items={items} />}
        {!collapsed && (
          <div className={styles['version']} title={commonStrings.menu.version}>
            {APP_VERSION}
          </div>
        )}
      </div>
      <div className={styles['content']}>
        <Outlet />
      </div>
    </div>
  );
}

export default Layout;
